import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import { showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import { Order } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { ChangeEvent, KeyboardEvent } from "react";
import { Orderstatus } from "components/Ordersorderstatus";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton, UpdateIconButton } from "components/DatatablesActionIcons";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { fetchParentHierarchy } from "utils/userDataFetcher";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
import { formatPaymentMethodLabel } from "../../utils/paymentMethodLabel";
import {
  PRODUCTION_STATUS_OPTIONS,
  getProductionStatusMeta,
} from "constants/productionStatus";

const DATE_OPTIONS = [
  { label: "Last 7 Days", value: "last_7_days" },
  { label: "Last 15 Days", value: "last_15_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "Custom", value: "custom" },
];

const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

function getOrderIdForNavigation(order: Order): string {
  const o = order as any;
  return String(o?.id ?? o?._id ?? o?.order_id ?? o?.orderId ?? "");
}

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999, position: "absolute", top: "100%", left: 0, right: 0 }),
  menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
};

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Order>();

const SignageOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.orders
  );
  const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [invoiceSentFilter, setInvoiceSentFilter] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("");
  const [productionStatusFilter, setProductionStatusFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [rowProductionStatusMap, setRowProductionStatusMap] = useState<Record<string, string>>({});
  const [updatingProductionRowKey, setUpdatingProductionRowKey] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [datePreset, setDatePreset] = useState<(typeof DATE_OPTIONS)[number] | null>(null); // default: Last 30 Days
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const fetchRequestIdRef = useRef(0);
  const hasLoadedDataRef = useRef(false);

  const { minDate, maxDate } = useMemo(() => {
    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(now.getMonth() - 6);
    return {
      minDate: sixMonthsAgo.toISOString().split("T")[0],
      maxDate: now.toISOString().split("T")[0],
    };
  }, []);

  useEffect(() => {
    if (datePreset?.value === "custom") {
      if (customFrom && customFrom < minDate) setCustomFrom("");
      if (customTo && customTo < minDate) setCustomTo("");
      if (customFrom && customFrom > maxDate) setCustomFrom("");
      if (customTo && customTo > maxDate) setCustomTo("");
    }
  }, [datePreset?.value, minDate, maxDate]);

  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy(token).then((res) => {
        if (res) {
          setParentSignageId(res.parentSignageId);
          setParentSignageName(res.parentSignageName);
        }
      });
    }
  }, [isSignageTeammember, token]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const handleAddClick = () => {
    setEditingOrderId(null);  // null means add mode
    setShowForm(true);
  };

  const handleEditClick = (orderId: string) => {
    setEditingOrderId(orderId); // pass orderId to form for edit mode
    setShowForm(true);
  };
  const fetchOrders = async () => {
    const requestId = ++fetchRequestIdRef.current;
    const showInitialLoader = !hasLoadedDataRef.current;
    setLoading(true);
    try {
      const hasSearch = Boolean(globalFilter?.trim());
      const dateRange = hasSearch
        ? { type: "", from: "", to: "" }
        : {
            type: datePreset?.value ?? "",
            from: datePreset?.value === "custom" ? customFrom : "",
            to: datePreset?.value === "custom" ? customTo : "",
          };
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orders/signage/${signageId}/datatable`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: globalFilter,
            filters: {
              invoice_sent: invoiceSentFilter || "",
              order_status: orderStatusFilter.toLowerCase(),
              payment_status: paymentStatusFilter,
              production_status: productionStatusFilter,
            },
            date_range: dateRange,
            sort: {
              field: "created_at",
              order: "desc",
            },
          }),
        }
      );

      if (!response.ok) showError("Access denied. You don’t have view permission for Sales orders.");
      const result = await response.json();
      if (requestId !== fetchRequestIdRef.current) return;
      setOrders(Array.isArray(result?.data) ? result.data : []);
      setFilteredOrders(Array.isArray(result?.data) ? result.data : []);
      setRowCount(typeof result?.total === "number" ? result.total : 0);
      hasLoadedDataRef.current = true;
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleExportCsv = async () => {
    if (!signageId || !token) return;
    try {
      setExporting(true);
      const url = `${process.env.REACT_APP_API_URL}/orders/signage/${signageId}/excel`;
      const body = {
        page: 1,
        limit: rowCount || 10000,
        search: globalFilter,
        filters: {
          invoice_sent: invoiceSentFilter || "",
          order_status: orderStatusFilter.toLowerCase(),
          payment_status: paymentStatusFilter,
          production_status: productionStatusFilter,
        },
        date_range: {
          type: datePreset?.value ?? "",
          from: datePreset?.value === "custom" ? customFrom : "",
          to: datePreset?.value === "custom" ? customTo : "",
        },
      };
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "orders.csv";
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export downloaded");
    } catch (err) {
      console.error("Error exporting:", err);
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const updateProductionStatus = async (
    orderId: string,
    productionStatus: string
  ) => {
    if (!orderId || !token) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orders/${orderId}/update_all_production_status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            production_status: productionStatus,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Failed to update production status");

      setOrders((prev) =>
        prev.map((row: any) => {
          const rid = String(row?.id ?? row?._id ?? row?.order_id ?? "");
          return rid === String(orderId) ? { ...row, production_status: productionStatus } : row;
        })
      );
      setFilteredOrders((prev) =>
        prev.map((row: any) => {
          const rid = String(row?.id ?? row?._id ?? row?.order_id ?? "");
          return rid === String(orderId) ? { ...row, production_status: productionStatus } : row;
        })
      );
      toast.success("Production status updated successfully!");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update production status");
    }
  };

  // Optionally fetch default on load if needed
  useEffect(() => {
    if (isSignageTeammember && !parentSignageId) return;
    if (!signageId || !token) return;
    fetchOrders();
  }, [
    signageId,
    token,
    isSignageTeammember,
    parentSignageId,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
  ]);

  // useEffect(() => {
  //   const fetchOrders = async () => {
  //     try {
  //       setLoading(true);
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/orders/signage/${userId}/datatable`,
  //         {
  //           method: "POST",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //           body: JSON.stringify({
  //             page: pagination.pageIndex + 1,
  //             limit: pagination.pageSize,
  //             search: globalFilter,
  //             // linked_status: "accepted",
  //             // current_status: "in_transit",
  //             filters: {
  //               order_status: orderStatusFilter.toLocaleLowerCase(),
  //               payment_status: paymentStatusFilter,
  //               production_status: productionStatusFilter
  //             },
  //             // date_range: {
  //             //   type: "last_15_days",
  //             //   from: "",
  //             //   to: ""
  //             // },
  //             sort: {
  //               field: "created_at",
  //               order: "desc"
  //             },
  //             // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
  //           })

  //         }
  //       );
  //       if (!response.ok) throw new Error("Failed to fetch orders data");
  //       const result = await response.json();
  //       setOrders(Array.isArray(result?.data) ? result.data : []);
  //       setFilteredOrders(Array.isArray(result?.data) ? result.data : []);
  //       setRowCount(typeof result?.total === 'number' ? result.total : 0);
  //     } catch (err) {
  //       console.error("Error fetching orders:", err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   if (userId) fetchOrders();
  // }, [userId, token, pagination.pageIndex, pagination.pageSize, globalFilter, orderStatusFilter, productionStatusFilter, paymentStatusFilter]);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const filtered = orders?.filter((order: any) => {
      const orderDate = new Date(order.order_date ?? order.created_at ?? 0);
      const matchesSearch =
        (order.order_number ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.customer_first_name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.customer_last_name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.agent_name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.brokerage_firm_name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.officeName ?? "").toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === "all" || String(order.order_status ?? order.status) === statusFilter;

      let matchesDate = true;
      if (dateFilter === "today") {
        matchesDate = orderDate >= today && orderDate < new Date(today.getTime() + 86400000);
      } else if (dateFilter === "yesterday") {
        const yesterday = new Date(today.getTime() - 86400000);
        matchesDate = orderDate >= yesterday && orderDate < today;
      } else if (dateFilter === "custom") {
        const start = customStartDate ? new Date(customStartDate) : null;
        const end = customEndDate ? new Date(customEndDate) : null;
        matchesDate = (!start || orderDate >= start) && (!end || orderDate <= end);
      }

      return matchesSearch && matchesStatus && matchesDate;
    });

    setFilteredOrders(filtered);
  }, [searchQuery, statusFilter, dateFilter, customStartDate, customEndDate, orders]);

  const columns = useMemo(() => {
    return [
      // columnHelper.display({
      //   id: "serial",
      //   header: () => <div className="th-text S-No">S.No.</div>,
      //   cell: (info) => (
      //     <div className="text-sm S-No">
      //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
      //     </div>
      //   )
      // }),
      // columnHelper.accessor(
      //   (row: Order) => row.order_number,
      //   {
      //     id: "order_number",
      //     header: () => (
      //       <div className="th-text S-No">Order ID</div>
      //     ),
      //     cell: (info) => (
      //       <div className="text-sm S-No order-link-1">{info.getValue()}</div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }),
      columnHelper.accessor("order_number", {
        id: "order_number",
        header: () => <div className="th-text S-No">Order ID</div>,
        cell: (info) => {
          const row = info.row.original as Order;
          // pick whichever your data uses:
          const orderId =
            (row as any).id ?? (row as any)._id ?? (row as any).order_id;

          return (
            <Link
              to={`${baseRoute}/orderDetails/${orderId}`}
              className="text-sm S-No order-link-1"
            >
              {info.getValue() ?? ""}
            </Link>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor(
        (row: Order) => {
          const dateVal = (row as any).order_date ?? (row as any).created_at;
          return dateVal ? new Date(dateVal).toLocaleDateString("en-US") : "--";
        },
        {
          id: "order_date",
          header: () => (
            <div className="th-text">Order Date</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }),
      columnHelper.accessor(
        (row: Order) => {
          const pn = String((row as any).project_number ?? "").trim();
          return pn || "--";
        },
        {
          id: "project_number",
          header: () => (
            <div className="th-text">Project Id</div>
          ),
          cell: (info) => {
            const value = String(info.getValue() ?? "").trim();
            const row = info.row.original as Order;
            const projectId = String(
              (row as any).project_id ?? (row as any).projectId ?? ""
            ).trim();
            const canOpenProject = Boolean(projectId && value && value !== "--");
            return (
              canOpenProject ? (
                <Link
                  to={`${baseRoute}/projects/view/${encodeURIComponent(projectId)}`}
                  className="text-sm order-link-1"
                >
                  {value}
                </Link>
              ) : (
                <div className="text-sm">{value || "--"}</div>
              )
            );
          },
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }),
      columnHelper.accessor(
        (row: Order) => row.brokerage_firm_name || "-",
        {
          id: "brokerage_firm_name",
          header: () => (
            <div className="th-text">Brokerage Firm</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }),
      columnHelper.accessor(
        (row: Order) => (row as any).officeName ?? (row as any).office?.officeName ?? "-",
        {
          id: "officeName",
          header: () => (
            <div className="th-text">Office</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }),
      columnHelper.accessor(
        (row: Order) => row.customer_email || "-",
        {
          id: "customer_email",
          header: () => (
            <div className="th-text">Email</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }),
      columnHelper.accessor(
        (row: Order) => {
          const agentName = (row as any).agent_name;
          if (agentName) return agentName;
          const fn = (row?.customer_first_name ?? "").trim();
          const ln = (row?.customer_last_name ?? "").trim();
          const full = [fn, ln].filter(Boolean).join(" ");
          return full || "--";
        },
        {
          id: "agent_name",
          header: () => <div className="th-text">Agent</div>,
          cell: (info) => {
            const val = info.getValue<string>();
            const isMissing = val === "--";
            return (
              <div className={`text-sm ${isMissing ? "text-muted" : ""}`}>
                {val}
              </div>
            );
          },
          filterFn: fuzzyTextFilter,       // still works for names; "--" won't match
          enableGlobalFilter: true,
          sortingFn: "alphanumeric",       // optional: sensible sorting
        }
      ),
            columnHelper.accessor("payment_status", {
        header: () => (
          <div className="th-text">Payment Status</div>
        ),
        cell: (info) => {
          const paymentStatus = info.getValue() as string;
          return (
            <div className="text-sm">
              {paymentStatus ? (
                <SalesOrderStatusBadge type="payment" value={paymentStatus} />
              ) : (
                "-"
              )}
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.accessor(
        (row: Order) => (row as any).order_status ?? (row as any).status,
        {
          id: "order_status",
          header: () => (
            <div className="th-text">Order Status</div>
          ),
          cell: (info) => {
            const orderStatus = info.getValue() as number;
            return (
              <div className="text-sm">
                {orderStatus ? (
                  <SalesOrderStatusBadge
                    type="order"
                    value={orderStatus}
                  />
                ) : (
                  "-"
                )}
              </div>
            );
          },
          enableGlobalFilter: false,
        }),
      columnHelper.accessor(
        (row: Order) => formatPaymentMethodLabel(row.payment_method),
        {
          id: "payment_method",
          header: () => <div className="th-text">Payment Method</div>,
          cell: (info) => <div className="text-sm">{info.getValue()}</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }),

      columnHelper.accessor(
        (row: Order) => {
          const paymentStatus = String((row as any)?.payment_status || "").trim().toLowerCase();
          if (paymentStatus === "paid") return "--";

          const paymentInfo = (row as any)?.payment_info;
          const last = Array.isArray(paymentInfo) && paymentInfo.length > 0 ? paymentInfo[paymentInfo.length - 1] : null;
          const dueDate = last?.due_date;
          if (!dueDate || String(dueDate).startsWith("0001-01-01")) return "--";
          const d = new Date(dueDate);
          return isNaN(d.getTime())
            ? "--"
            : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        },
        {
          id: "payment_due_date",
          header: () => (
            <div className="th-text">Payment Due Date</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          enableGlobalFilter: false,
        }
      ),

      columnHelper.accessor(
        (row: Order) => {
          const status = String((row as any)?.quote_status ?? "").trim().toLowerCase();
          if (!status) return "-";
          return status
            .split("_")
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");
        },
        {
          id: "quote_status",
          header: () => <div className="th-text">Quote Status</div>,
          cell: (info) => <div className="text-sm">{info.getValue()}</div>,
          enableGlobalFilter: false,
        }
      ),

      columnHelper.accessor(
        (row: Order) => {
          const raw = String((row as any)?.invoice_sent ?? "").trim().toLowerCase();
          return raw === "yes" || raw === "true" || raw === "1";
        },
        {
          id: "invoice_sent",
          header: () => <div className="th-text">Invoice Sent</div>,
          cell: (info) => {
            const sent = info.getValue<boolean>();
            return (
              <div className="text-sm">
                {sent ? (
                 <input
                 type="checkbox"
                 checked
                 disabled
                 readOnly
                 className="form-check-input invoice-sent-checkbox border-success"
                 style={{
                   width: "1.2rem",
                   height: "1.2rem",
                   cursor: "not-allowed",
                 }}
                 title="Yes"
               />
                ) : (
                  "-"
                )}
              </div>
            );
          },
          enableGlobalFilter: false,
        }
      ),

      columnHelper.accessor(
        (row: Order) => row.signage_company_name || "-",
        {
          id: "signage_company_name",
          header: () => (
            <div className="th-text">Vendor</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }),
      columnHelper.display({
        id: "production_status",
        header: () => <div className="th-text">Production Status</div>,
        cell: (info) => {
          const order = info.row.original as any;
          const rowKey = String(order?.id ?? order?._id ?? order?.order_id ?? info.row.id);
          const currentRaw = rowProductionStatusMap[rowKey] ?? order?.production_status ?? "not_started";
          const meta = getProductionStatusMeta(currentRaw);
          const orderId = String(order?.id ?? order?._id ?? order?.order_id ?? "");
          const isUpdatingThisRow = updatingProductionRowKey === rowKey;
          return (
            <div className="text-sm d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm production-status-select"
                value={meta.key}
                onChange={(e) => {
                  const next = e.target.value;
                  setRowProductionStatusMap((prev) => ({ ...prev, [rowKey]: next }));
                }}
                style={{
                  ["--ps-border" as any]: meta.style.border,
                  ["--ps-bg" as any]: meta.style.bg,
                  ["--ps-color" as any]: meta.style.color,
                  ["--sel-border" as any]: meta.style.border,
                  ["--sel-bg" as any]: meta.style.bg,
                  ["--sel-radius" as any]: "10px",
                  minWidth: 180,
                  maxWidth: 200,
                  height: 40,
                  borderRadius: 10,
                  fontSize: 14,
                }}
              >
                {PRODUCTION_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <UpdateIconButton
                title="Update"
                className={`icon-square ${isUpdatingThisRow ? "production-status-updating" : ""}`}
                onClick={async () => {
                  if (!orderId) return;
                  setUpdatingProductionRowKey(rowKey);
                  try {
                    await updateProductionStatus(orderId, meta.key);
                  } finally {
                    setUpdatingProductionRowKey((prev) => (prev === rowKey ? null : prev));
                  }
                }}
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <div className="th-text">Actions</div>
        ),
        cell: (info) => {
          const orderInfo1 = info.row.original as any;
          const orderId = orderInfo1?.id ?? orderInfo1?._id;
          const s = orderInfo1?.order_status ?? orderInfo1?.status;

          const statusAllowsEdit =
            typeof s === "number" && s <= 300 && s !== 302 && s !== 401 && s !== 400;

          return (
            <div className="d-flex  align-items-center gap-2">
              {canView && (
                <ViewIconButton
                  to={`${baseRoute}/orderDetails/${orderId}`}
                  title="View"
                  className="icon-square"
                />
              )}

              {canEdit && statusAllowsEdit && (
                <EditIconButton
                  to={`${baseRoute}/editOrder/${orderId}`}
                  title="Edit"
                  className="icon-square"
                />
              )}

            </div>
          );
        },
      }),
    ];
  }, [baseRoute, deleting, pagination, globalFilter, rowProductionStatusMap, token, updatingProductionRowKey, canView, canEdit]);



  const table = useReactTable({
    data: orders,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;
  const showTableLoader = loading;

  return (
    <div className="content">
      <style>{`
        .production-status-select {
          border: 1px solid var(--ps-border) !important;
          background-color: var(--ps-bg) !important;
          color: var(--ps-color) !important;
        }
        .production-status-updating .sqbtn__icon {
          animation: productionStatusSpin 0.75s linear infinite;
        }
        @keyframes productionStatusSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 ">
        {/* <h4 className="mb-0 fw-bold">Orders</h4> */}
        <h4></h4>
        <div className="d-flex gap-3 align-items-center">
          {/* <Link to={`${baseRoute}/docs/orders`} className="btn btn-outline-secondary btn-sm">
            Docs &amp; Routes
          </Link> */}
          {canCreate && (
            <Link to={`${baseRoute}/createOrder`} className="btn btn-primary  table-addbutton">
            + Add Order
          </Link>
          )}

        </div>
      </div>
      <div className="po-header mb-4 shadow-border-datatables">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Orders</h4>
            {/* <div className="po-subtitle">Manage incoming products from suppliers and brokerages</div> */}
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <form
              className="input-group po-search"
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                setGlobalFilter(searchInput);
              }}
            >
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              {/* Browsers often ignore autocomplete="off" on filters; new-password suppresses Chrome autofill here. */}
              <input
                type="search"
                id="signage-orders-search"
                name="signage_orders_filter"
                className="form-control"
                placeholder="Search by order #, firm, contact, brokerages…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setGlobalFilter(searchInput);
                }}
                autoComplete="new-password"
                data-lpignore="true"
              />
            </form>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="incoming-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            <button
              type="button"
              className="export-button"
              onClick={handleExportCsv}
              disabled={exporting}
            >
              <i className="ri-file-excel-2-line me-2" />
              {exporting ? "Exporting…" : "Export to Excel"}
            </button>

          </div>
        </div>

        <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex"> {/* <-- flex wrapper */}
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-12 col-md-auto">
                <label htmlFor="invoiceSentFilter" className="form-label mb-1 label-1">Invoice Status</label>
                <select
                  id="invoiceSentFilter"
                  className="form-select"
                  value={invoiceSentFilter}
                  onChange={(e) => setInvoiceSentFilter(e.target.value)}
                >
                  <option value="">Invoice Status</option>
                  <option value="yes">Invoice Sent</option>
                  <option value="no">Not Sent</option>
                </select>
              </div>

              <div className="col-12 col-md-auto">
                <label htmlFor="linkedStatusFilter" className="form-label mb-1 label-1">Order Status</label>
                <select
                  className="form-select "
                  value={orderStatusFilter}
                  onChange={(e) => setOrderStatusFilter(e.target.value)}
                >
                  <option value="">Order Status</option>
                  <option value="100">Draft</option>
                  <option value="101">Confirmed</option>
                  {/* <option value="300">Shipping</option> */}
                  <option value="301">Shipped</option>
                  <option value="302">Delivered</option>
                  <option value="400">Cancelled</option>
                </select>
              </div>

              <div className="col-12 col-md-auto">
                <label htmlFor="currentStatusFilter" className="form-label mb-1 label-1">Production Status</label>
                <select
                  className="form-select "
                  value={productionStatusFilter}
                  onChange={(e) => setProductionStatusFilter(e.target.value)}
                >
                  <option value="">Production Status</option>
                  <option value="Not Started">Not Started</option>
                  <option value="In Production">In Production</option>
                  <option value="Quality Check">Quality Check</option>
                  <option value="Ready for Installation">Ready for Installation</option>
                </select>
              </div>
              <div className="col-12 col-md-auto">
                <label htmlFor="currentStatusFilter" className="form-label mb-1 label-1">Payment Status</label>
                <select
                  className="form-select "
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                >
                  <option value="">Payment Status</option>
                  <option value="Paid">Paid</option>
                  <option value="Partial Payment">Partial Payment</option>
                  <option value="Pending">Pending</option>
                  <option value="Overdue">Overdue</option>
                </select>
              </div>
              <div className="col-12 col-md-auto">
                <label className="form-label">Date Range</label>
                <Select
                  options={DATE_OPTIONS}
                  value={datePreset}
                  onChange={(val) => setDatePreset(val ?? null)}
                  placeholder="Select date range"
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                  classNamePrefix="rs"
                />
              </div>
              {datePreset?.value === "custom" && (
                <>
                  <div className="col-md-2">
                    <label className="form-label">From</label>
                    <input type="date" className="form-control br-8" value={customFrom}
                      min={minDate} max={maxDate}
                      onChange={e => setCustomFrom(e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">To</label>
                    <input type="date" className="form-control br-8" value={customTo}
                      min={minDate} max={maxDate}
                      onChange={e => setCustomTo(e.target.value)} />
                  </div>
                </>
              )}

              <div className={`col-12 col-md-auto d-flex justify-content-md-end gap-2 ${datePreset?.value === "custom" ? "mt-3" : "mt-1 mt-md-0"}`}>
                <button
                  className="btn btn-primary table-addbutton flex-fill flex-md-grow-0"
                  onClick={() => {
                    // Optional: jump back to first page before fetch
                    setPagination(prev => ({ ...prev, pageIndex: 0 }));
                    fetchOrders();
                  }}
                >
                  <i className="ri-check-line me-1" />
                  Apply Filter
                </button>

                <button
                  className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-lg-grow-0"
                  onClick={() => {
                    fetchOrders()
                    setInvoiceSentFilter("");
                    setOrderStatusFilter("");
                    setProductionStatusFilter("");
                    setPaymentStatusFilter("");
                    setDatePreset(null);
                    setCustomFrom("");
                    setCustomTo("");
                    setSearchInput("");
                    setGlobalFilter("");              // clear server-side search
                    setPagination({ pageIndex: 0, pageSize: pagination.pageSize }); // go to first page
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 ">Sales Orders</h5>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            {/* orders */}
          </span>

        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {showTableLoader ? (
                <DesktopTableLoader table={table} message="Loading orders…" variant="dots" />
              ) : (
                <DesktopTableParts
                  table={table}
                  onRowClick={(row) => {
                    const orderId = getOrderIdForNavigation(row);
                    if (!orderId) return;
                    navigate(`${baseRoute}/orderDetails/${orderId}`);
                  }}
                />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile shadow-sm">
          {showTableLoader ? (
            <MobileCardsLoader message="Loading orders…" variant="dots" />
          ) : (
            <MobileCardsOnly
              table={table}
              idColId="order_link"
              titleColId="product_name"
              actionColId="actions"
              onRowClick={(row) => {
                const orderId = getOrderIdForNavigation(row);
                if (!orderId) return;
                navigate(`${baseRoute}/orderDetails/${orderId}`);
              }}
            />
          )}
        </div>
      </div>
      <DataTableStatus
        loading={showTableLoader}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
    </div>

  );
};

export default SignageOrders;
